import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, AlertTriangle, ExternalLink, Loader2, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import { STATE_NAMES } from '../data/usStates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEST_POLICY_LABELS = {
  A: 'Always required', F: 'Test flexible', I: 'Tests ignored',
  N: 'Never required', S: 'Sometimes required',
};

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-[11px] font-medium tracking-wide border transition-all ${
        active
          ? 'bg-[#0F392B] text-white border-[#0F392B] shadow-sm'
          : 'bg-white text-[#0F392B] border-[#E2E2E2] hover:border-[#0F392B]/40'
      }`}
    >
      {children}
    </button>
  );
}

function Cell({ value, muted = false }) {
  if (!value || value === 'None') return <span className="text-[#C9C9C0]">—</span>;
  if (value === 'See Website') return <span className="text-[11px] text-muted-foreground italic">See site</span>;
  return <span className={`text-[12px] ${muted ? 'text-muted-foreground' : 'text-foreground'}`}>{value}</span>;
}

const hasVal = (v) => !!v && v !== 'None';

export default function DeadlinesStatePage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const stateCode = (code || '').toUpperCase();
  const stateName = STATE_NAMES[stateCode];

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null); // 'ed' | 'ea' | 'rea' | 'rolling'
  const [states, setStates] = useState([]);

  useEffect(() => {
    if (!stateName) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    setPlan(null);
    fetch(`${API_URL}/api/common-app/requirements?state=${stateCode}&limit=500`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setItems(d.items || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [stateCode, stateName]);

  useEffect(() => {
    fetch(`${API_URL}/api/common-app/states`)
      .then((r) => r.json())
      .then((d) => setStates(d.items || []))
      .catch(() => {});
  }, []);

  const counts = useMemo(() => ({
    ed: items.filter((r) => hasVal(r.ed) || hasVal(r.edii)).length,
    ea: items.filter((r) => hasVal(r.ea) || hasVal(r.eaii)).length,
    rea: items.filter((r) => hasVal(r.rea)).length,
    rolling: items.filter((r) => hasVal(r.rd_rolling)).length,
    free: items.filter((r) => (r.fee_us || '').replace(/[^0-9.]/g, '') === '0').length,
  }), [items]);

  const filtered = useMemo(() => {
    if (!plan) return items;
    if (plan === 'ed') return items.filter((r) => hasVal(r.ed) || hasVal(r.edii));
    if (plan === 'ea') return items.filter((r) => hasVal(r.ea) || hasVal(r.eaii));
    if (plan === 'rea') return items.filter((r) => hasVal(r.rea));
    if (plan === 'rolling') return items.filter((r) => hasVal(r.rd_rolling));
    return items;
  }, [items, plan]);

  if (!stateName) {
    return (
      <div className="min-h-screen bg-white">
        <SEO title="State not found — Common App Deadlines" noindex />
        <MarketingNav />
        <div className="pt-32 pb-24 max-w-2xl mx-auto px-4 text-center" data-testid="state-not-found">
          <AlertTriangle className="h-10 w-10 text-[#D4AF37] mx-auto mb-4" strokeWidth={1.5} />
          <h1 className="text-2xl font-semibold text-[#0F392B] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            State not found
          </h1>
          <p className="text-sm text-muted-foreground mb-6">
            &ldquo;{code}&rdquo; isn&apos;t a US state code we recognize. Browse the full grid instead.
          </p>
          <Link to="/deadlines-grid">
            <Button className="bg-[#0F392B] text-white h-11 px-6 text-sm font-semibold">
              Back to the full grid <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Common App Deadlines Grid", "item": "https://ivyleagueconsulting.org/deadlines-grid" },
        { "@type": "ListItem", "position": 2, "name": `Colleges in ${stateName}`, "item": `https://ivyleagueconsulting.org/deadlines-grid/state/${stateCode.toLowerCase()}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": `Common App Colleges in ${stateName}`,
      "numberOfItems": items.length,
      "itemListElement": items.slice(0, 50).map((r, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "name": r.name,
        "url": `https://ivyleagueconsulting.org/deadlines-grid/${r.slug}`,
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="state-page">
      <SEO
        title={`Colleges in ${stateName} — Common App Deadlines & Fees 2025-26${items.length ? ` (${items.length} Schools)` : ''}`}
        description={`Every Common App college in ${stateName}: compare Early Decision, Early Action, REA, and rolling admissions deadlines, application fees, and test policies for the 2025-26 cycle.${counts.rolling ? ` ${counts.rolling} ${stateName} colleges offer rolling admissions.` : ''}`}
        keywords={`colleges in ${stateName.toLowerCase()} with rolling admissions, ${stateName.toLowerCase()} college application deadlines, common app schools in ${stateName.toLowerCase()}, ${stateName.toLowerCase()} early action colleges, ${stateName.toLowerCase()} early decision deadlines`}
        schema={schema}
      />
      <MarketingNav />

      {/* Breadcrumb */}
      <div className="pt-20 pb-4 bg-[#F9F9F7] border-b border-[#E2E2E2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/deadlines-grid" className="text-xs text-muted-foreground hover:text-[#0F392B] inline-flex items-center gap-1.5" data-testid="state-breadcrumb-back">
            <ArrowLeft className="h-3 w-3" /> Common App grid
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section className="py-12 md:py-16 bg-[#0F392B] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-3 flex items-center gap-2">
            <MapPin className="h-4 w-4" /> Common App 2025-26 · By State
          </p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.1] mb-4" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="state-page-title">
            Colleges in <span className="italic text-[#D4AF37]">{stateName}</span>
          </h1>
          <p className="text-base text-white/70 max-w-2xl leading-relaxed">
            {loading ? 'Loading…' : `${items.length} Common App member colleges in ${stateName}. Every ED, EA, REA, and rolling deadline — with fees and test policies.`}
          </p>
          {!loading && items.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-3xl mt-8" data-testid="state-stats">
              <div><p className="text-2xl font-semibold text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>{items.length}</p><p className="text-[11px] text-white/60 uppercase tracking-widest">Colleges</p></div>
              <div><p className="text-2xl font-semibold text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>{counts.ed}</p><p className="text-[11px] text-white/60 uppercase tracking-widest">Offer ED</p></div>
              <div><p className="text-2xl font-semibold text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>{counts.ea}</p><p className="text-[11px] text-white/60 uppercase tracking-widest">Offer EA</p></div>
              <div><p className="text-2xl font-semibold text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>{counts.rolling}</p><p className="text-[11px] text-white/60 uppercase tracking-widest">RD / Rolling</p></div>
              <div><p className="text-2xl font-semibold text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>{counts.free}</p><p className="text-[11px] text-white/60 uppercase tracking-widest">$0 App Fee</p></div>
            </div>
          )}
        </div>
      </section>

      {/* Filters + Table */}
      <section className="py-12 md:py-16" data-testid="state-directory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
            <div className="flex flex-wrap gap-2" data-testid="state-plan-filters">
              <Chip active={!plan} onClick={() => setPlan(null)} testid="state-plan-all">All plans</Chip>
              <Chip active={plan === 'ed'} onClick={() => setPlan('ed')} testid="state-plan-ed">Early Decision ({counts.ed})</Chip>
              <Chip active={plan === 'ea'} onClick={() => setPlan('ea')} testid="state-plan-ea">Early Action ({counts.ea})</Chip>
              <Chip active={plan === 'rea'} onClick={() => setPlan('rea')} testid="state-plan-rea">REA ({counts.rea})</Chip>
              <Chip active={plan === 'rolling'} onClick={() => setPlan('rolling')} testid="state-plan-rolling">RD / Rolling ({counts.rolling})</Chip>
            </div>
            <p className="text-xs text-muted-foreground" data-testid="state-count">
              Showing <span className="text-[#0F392B] font-semibold">{filtered.length}</span> of {items.length} {stateName} colleges
            </p>
          </div>

          <div className="rounded-2xl border border-[#E2E2E2] bg-white shadow-sm overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground text-sm gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading colleges…
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16" data-testid="state-empty">
                <p className="text-base text-[#0F392B] font-medium mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  No {stateName} colleges match that plan.
                </p>
                <Button onClick={() => setPlan(null)} variant="outline" className="border-[#0F392B] text-[#0F392B]">Show all plans</Button>
              </div>
            ) : (
              <table className="w-full text-sm min-w-[900px]" data-testid="state-table">
                <thead>
                  <tr className="border-b border-[#E2E2E2] bg-[#F9F9F7]">
                    <th className="text-left py-3 px-4 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">College</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">ED</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">EDII</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">EA</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">REA</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">RD / Rolling</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Fee (US / INTL)</th>
                    <th className="text-left py-3 px-3 text-[11px] uppercase tracking-wide text-muted-foreground font-semibold" title="Test Policy">Tests</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => (
                    <tr key={r.slug} className={`border-b border-[#E2E2E2] last:border-b-0 hover:bg-[#F9F9F7]/60 cursor-pointer ${i % 2 === 1 ? 'bg-[#F9F9F7]/30' : ''}`} data-testid={`state-row-${r.slug}`} onClick={() => navigate(`/deadlines-grid/${r.slug}`)}>
                      <td className="py-3 px-4">
                        <p className="text-[13px] font-semibold text-[#0F392B] hover:underline" style={{ fontFamily: "'Playfair Display', serif" }}>{r.name}</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">{r.school_type || 'Coed'}</p>
                      </td>
                      <td className="py-3 px-3"><Cell value={r.ed} /></td>
                      <td className="py-3 px-3"><Cell value={r.edii} /></td>
                      <td className="py-3 px-3"><Cell value={r.ea} /></td>
                      <td className="py-3 px-3"><Cell value={r.rea} /></td>
                      <td className="py-3 px-3"><Cell value={r.rd_rolling} /></td>
                      <td className="py-3 px-3">
                        <p className="text-[12px] text-foreground">{r.fee_us || '—'}{r.fee_intl && r.fee_intl !== r.fee_us && <span className="text-muted-foreground"> / {r.fee_intl}</span>}</p>
                      </td>
                      <td className="py-3 px-3" title={TEST_POLICY_LABELS[r.test_policy] || ''}>
                        {r.test_policy ? (
                          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${
                            r.test_policy === 'A' ? 'bg-[#0F392B] text-white' :
                            r.test_policy === 'N' || r.test_policy === 'I' ? 'bg-[#F9F9F7] text-[#0F392B] border border-[#E2E2E2]' :
                            'bg-[#D4AF37]/20 text-[#0F392B]'
                          }`}>{r.test_policy}</span>
                        ) : <span className="text-[#C9C9C0]">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <p className="text-[11px] text-muted-foreground text-center italic mt-6">
            Source: Common Application 2025-26 first-year grid, updated Oct 24 2025. Always verify with each college before submitting.
          </p>
        </div>
      </section>

      {/* Browse other states */}
      {states.length > 0 && (
        <section className="py-14 bg-[#F9F9F7] border-y border-[#E2E2E2]" data-testid="state-browse-others">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl md:text-2xl font-medium text-[#0F392B] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Browse other states
            </h2>
            <div className="flex flex-wrap gap-2">
              {states.filter((s) => s.state !== stateCode).map((s) => (
                <Link
                  key={s.state}
                  to={`/deadlines-grid/state/${s.state.toLowerCase()}`}
                  className="px-3 py-1.5 rounded-full text-[11px] font-medium bg-white text-[#0F392B] border border-[#E2E2E2] hover:border-[#0F392B]/40 transition-all"
                  data-testid={`state-link-${s.state.toLowerCase()}`}
                >
                  {STATE_NAMES[s.state] || s.state} <span className="opacity-60">({s.count})</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-[#0F392B]" data-testid="state-cta-section">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Applying to a {stateName} college?
          </h2>
          <p className="text-sm text-white/70 mb-8 max-w-md mx-auto">
            Add any school on this page to your Ivy League Admissions Portal and every deadline, fee, and test policy auto-syncs.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/sign-up">
              <Button size="lg" className="bg-[#D4AF37] text-[#0F392B] hover:bg-[#D4AF37]/90 h-12 px-8 font-semibold shadow-xl" data-testid="state-cta-platform">
                Start the Platform — $99 <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/deadlines-grid">
              <Button size="lg" variant="outline" className="h-12 px-8 border-white/40 text-white hover:bg-white hover:text-[#0F392B]" data-testid="state-cta-grid">
                Browse the full grid <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
